import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useCompany } from "../companies/CompanyContext";
import { useBranding } from "../branding/BrandingContext";
import { useTranslation } from "react-i18next";

function Header({ onToggleSidebar }) {
  const { isAuthenticated, user } = useAuth();
  useCompany();
  const { branding } = useBranding();
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const activeLanguage = i18n.resolvedLanguage?.startsWith("en") ? "en" : "de";
  // Header-Hintergrund ist weiss -> bevorzugt die helle (blaue) Logo-Variante.
  // Standard-Logo ist sprachabhaengig (DE/EN Variante).
  const defaultLogoSrc =
    activeLanguage === "en"
      ? "/data-optimizer-logo-blue-en.webp"
      : "/data-optimizer-logo-blue-de.webp";
  const logoSrc =
    branding?.logo_url ||
    branding?.logo_url_dark ||
    defaultLogoSrc;

  const nextLanguage = activeLanguage === "de" ? "en" : "de";
  // Versions-Suffix erzwingt neues Laden nach Bildwechsel (Cache-Busting),
  // da die PNGs 30 Tage im Browser-/CDN-Cache liegen. Bei Bildtausch erhoehen.
  const FLAG_ASSET_VERSION = "2";
  const activeLanguageFlag =
    (activeLanguage === "de" ? "/flag-de.png" : "/flag-uk.png") +
    `?v=${FLAG_ASSET_VERSION}`;
  const languageSwitchLabel =
    nextLanguage === "en"
      ? t("header.switchToEnglish")
      : t("header.switchToGerman");

  const handleLanguageToggle = () => {
    i18n.changeLanguage(nextLanguage);
  };

  // Dropdown bei Klick ausserhalb oder Escape schliessen.
  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const handlePointer = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  return (
    <header>
      <Link to="/"><img className="login-logo" alt={t("header.logoAlt")} src={logoSrc} /></Link>
      <div className="header-right">
        {isAuthenticated ? (
          <div className="profile-menu" ref={menuRef}>
            <button
              type="button"
              className="profile-menu-trigger"
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t("header.openProfileMenu")}
            >
              {user?.avatar_url ? (
                <img
                  className="profile-avatar"
                  alt={t("header.profileAlt")}
                  src={user.avatar_url}
                />
              ) : (
                <img
                  className="profile-logo"
                  alt={t("header.profileAlt")}
                  src="/profile-icon-blue.png"
                />
              )}
            </button>
            {menuOpen && (
              <div className="profile-menu-dropdown" role="menu">
                <Link
                  to="/tasks"
                  className="profile-menu-item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="profile-menu-item-icon" aria-hidden="true">
                    <img
                      src="/data-optimizer-icon-task-management.svg"
                      alt=""
                      width="16"
                      height="16"
                    />
                  </span>
                  {t("header.menuTasks")}
                </Link>
                <Link
                  to="/profile"
                  className="profile-menu-item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="profile-menu-item-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z"
                      />
                    </svg>
                  </span>
                  {t("header.menuProfile")}
                </Link>
                <button
                  type="button"
                  className="profile-menu-item"
                  role="menuitem"
                  onClick={() => {
                    handleLanguageToggle();
                    setMenuOpen(false);
                  }}
                >
                  <img
                    className="profile-menu-item-flag"
                    src={activeLanguageFlag}
                    alt=""
                    aria-hidden="true"
                  />
                  {languageSwitchLabel}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={handleLanguageToggle}
            className="language-toggle"
            aria-label={languageSwitchLabel}
            title={languageSwitchLabel}
          >
            <img className="language-flag" src={activeLanguageFlag} alt="" aria-hidden="true" />
          </button>
        )}
        {isAuthenticated && onToggleSidebar && (
          <button onClick={onToggleSidebar} className="hamburger" aria-label={t("header.toggleSidebar")}>☰</button>
        )}
      </div>
    </header>
  );
}

export default Header;